import React, { useContext, useEffect, useState } from "react";
import { BuilderStoreContext } from "@builder.io/react";

export interface StepperProps {
  segments: number;
  fillTime: number; // in milliseconds
  stateVariable: string;
}

const Stepper: React.FC<StepperProps> = ({
  segments,
  fillTime,
  stateVariable,
}) => {
  // Access the Builder.io context.
  const builderState = useContext(BuilderStoreContext);

  // currentSegment: the index (0-based) of the segment that is currently animating.
  // All segments with index less than currentSegment are rendered as fully filled.
  const [currentSegment, setCurrentSegment] = useState<number>(0);
  // resetTrigger is used to force a re-mount of the animated element when needed.
  const [resetTrigger, setResetTrigger] = useState<number>(0);

  // Update the Builder.io state variable when currentSegment changes.
  useEffect(() => {
    if (
      builderState &&
      builderState.rootState &&
      builderState.rootState[stateVariable] !== currentSegment
    ) {
      builderState.update((state) => {
        state[stateVariable] = currentSegment;
      });
    }
  }, [currentSegment, stateVariable]);

  // When the current segment's animation ends, move to the next segment.
  // If the last segment completes, reset to segment 0.
  const handleAnimationEnd = () => {
    if (currentSegment >= segments - 1) {
      setCurrentSegment(0);
      setResetTrigger((prev) => prev + 1);
    } else {
      setCurrentSegment((prev) => prev + 1);
    }
  };

  // When clicking on any segment:
  // 1. Stop any current animation by forcing a re-mount (via resetTrigger).
  // 2. Set the clicked segment as current, so that it begins animating.
  // 3. Instantly render segments before as full and segments after as empty.
  const handleSegmentClick = (index: number) => {
    setCurrentSegment(index);
    setResetTrigger((prev) => prev + 1);
  };

  return (
    <>
      {/* CSS keyframes for the fill animation */}
      <style>{`
        @keyframes fillAnimation {
          0% { width: 0%; }
          100% { width: 100%; }
        }
      `}</style>

      {/* Stepper container */}
      <div className="flex gap-1">
        {Array.from({ length: segments }, (_, index) => (
          <div
            key={index}
            onClick={() => handleSegmentClick(index)}
            className="grow shrink basis-0 h-[3px] bg-[#132527]/20 rounded-[5px] cursor-pointer"
          >
            {/* Render segments before the current one as fully filled */}
            {index < currentSegment && (
              <div
                className="h-full bg-[#122427] rounded-[30px]"
                style={{ width: "100%" }}
              />
            )}

            {/* Render the current segment with an animated fill */}
            {index === currentSegment && (
              <div
                // Changing the key (using resetTrigger) forces a re-mount to restart the animation.
                key={`anim-${currentSegment}-${resetTrigger}`}
                className="h-full bg-[#122427] rounded-[30px]"
                style={{
                  animation: `fillAnimation ${fillTime}ms linear forwards`,
                }}
                onAnimationEnd={handleAnimationEnd}
              />
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default Stepper;
